/**
 * =============================================================================
 * APP - Configuração de Rotas
 * =============================================================================
 *
 * Este ficheiro define todas as rotas da aplicação usando BrowserRouter,
 * o que produz URLs amigáveis para SEO (sem /#/). O servidor Apache deve
 * estar configurado com fallback para index.html (ver public/.htaccess).
 *
 * Rotas disponíveis:
 * - /                     → Home (Início)
 * - /curriculo            → Currículo
 * - /hipnoterapia         → Hipnoterapia
 * - /super-aprendizagem   → Super Aprendizagem
 * - /formacao             → Formação
 * - /pro-bono             → Pro Bono
 * - /psicologia           → Psicologia
 * - /livros/manual-para-professores → Livro Manual
 * - /livros/ser-maior     → Livro Ser Maior
 * - /privacidade          → Política de Privacidade
 * - /termos-e-condicoes   → Termos e Condições
 * - *                     → Página 404 (Não encontrada)
 *
 * Todas as rotas são envolvidas pelo Layout (Navbar + Footer) e o tema
 * é forçado para claro (light) sem alternador.
 *
 * NOTA: o Apache só devolve 200 para as rotas listadas em public/.htaccess
 * (o resto recebe 404 real). Ao adicionar uma rota nova, atualizar também
 * a RewriteRule das rotas válidas nesse ficheiro.
 */

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { CookieProvider } from "@/context/CookieProvider";
import Layout from "./components/Layout";
import { PageLoader } from "./components/PageLoader";
import { ScrollToTop } from "./components/ScrollToTop";

// Importação das páginas
// Home permanece estática para carregamento inicial imediato
import Home from "./pages/Home";

// Páginas secundárias carregadas sob demanda (code-splitting)
const Curriculo = lazy(() => import("./pages/Curriculo"));
const Hipnoterapia = lazy(() => import("./pages/Hipnoterapia"));
const SuperAprendizagem = lazy(() => import("./pages/SuperAprendizagem"));
const Formacao = lazy(() => import("./pages/Formacao"));
const ProBono = lazy(() => import("./pages/ProBono"));
const Psicologia = lazy(() => import("./pages/Psicologia"));
const LivroManual = lazy(() => import("./pages/LivroManual"));
const LivroSerMaior = lazy(() => import("./pages/LivroSerMaior"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const NotFound = lazy(() => import("./pages/NotFound"));

const GTM_ID = import.meta.env.VITE_GTM_ID ?? "";
const GA_ID = import.meta.env.VITE_GA_ID ?? "";

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light">
      <CookieProvider gtmId={GTM_ID} gaId={GA_ID} language="pt">
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Layout envolve todas as rotas (Navbar + Footer) */}
              <Route element={<Layout />}>
                {/* Rota principal - Início */}
                <Route path="/" element={<Home />} />

                {/* Currículo */}
                <Route path="/curriculo" element={<Curriculo />} />

                {/* Serviços */}
                <Route path="/hipnoterapia" element={<Hipnoterapia />} />
                <Route path="/super-aprendizagem" element={<SuperAprendizagem />} />
                <Route path="/formacao" element={<Formacao />} />

                {/* Pro Bono */}
                <Route path="/pro-bono" element={<ProBono />} />

                {/* Psicologia */}
                <Route path="/psicologia" element={<Psicologia />} />

                {/* Livros Editados */}
                <Route path="/livros/manual-para-professores" element={<LivroManual />} />
                <Route path="/livros/ser-maior" element={<LivroSerMaior />} />

                {/* Política de Privacidade */}
                <Route path="/privacidade" element={<Privacidade />} />

                {/* Termos e Condições */}
                <Route path="/termos-e-condicoes" element={<Termos />} />

                {/* Rota de fallback - página 404 */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CookieProvider>
    </ThemeProvider>
  );
}
